import React, { useEffect, useState } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonBadge,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonButtons,
  IonBackButton,
  IonButton,
  IonModal,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonChip,
  IonIcon,
  IonText,
} from "@ionic/react";
import {
  personOutline,
  timeOutline,
  chevronForwardOutline,
  shieldCheckmarkOutline,
  alertCircleOutline,
  banOutline,
  refreshOutline,
} from "ionicons/icons";
import { baseUrl, config, requestID } from "../../../common/utils";
import axios from "axios";
// import "./UserManagement.css"; // Create this CSS file for custom styles

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Modern color scheme
  const colors = {
    primary: "#3880ff",
    secondary: "#3dc2ff",
    success: "#2dd36f",
    warning: "#ffc409",
    danger: "#eb445a",
    medium: "#92949c",
    dark: "#222428",
    light: "#f4f5f8",
  };

  const getallUsers = async () => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("requestID", requestID());
      const response = await axios.post(
        baseUrl() + "Beneficiarys/ss_getallUsers",
        formData,
        config
      );
      setUsers(response.data.data);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getAuditTrails = async () => {
    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      const response = await axios.post(
        baseUrl() + "Audittrails/ss_getAuditTrails",
        formData,
        config
      );
      setAuditLogs(response.data.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getallUsers();
    getAuditTrails();
  }, []);

  const assignRole = async (user: any) => {
    try {
      setIsUpdating(true);
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("roleID", user.roleID);
      formData.append("user_id", user.user_id);

      await axios.post(
        baseUrl() + "Beneficiarys/ss_assign_role",
        formData,
        config
      );

      // Refresh data
      await getallUsers();
      setShowModal(false);
    } catch (error) {
      console.log(error);
    } finally {
      setIsUpdating(false);
    }
  };

  const accountStatus = async (user: any) => {
    try {
      setIsUpdating(true);
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("status", user.account_status);
      formData.append("user_id", user.user_id);

      await axios.post(
        baseUrl() + "Beneficiarys/ss_account_status",
        formData,
        config
      );

      // Refresh data
      await getallUsers();
      setShowModal(false);
    } catch (error) {
      console.log(error);
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "APPROVED":
        return shieldCheckmarkOutline;
      case "suspended":
        return alertCircleOutline;
      case "Banned":
        return banOutline;
      default:
        return personOutline;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "APPROVED":
        return colors.success;
      case "suspended":
        return colors.warning;
      case "Banned":
        return colors.danger;
      default:
        return colors.medium;
    }
  };

  // Calculate pagination values
  const totalUsers = users?.length;
  const totalPages = Math.ceil(totalUsers / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = users.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  return (
    <IonPage className="modern-user-management, pushDown">
      <IonHeader className="modern-header">
        <IonToolbar style={{ "--background": colors.primary }}>
          <IonButtons slot="start">
          </IonButtons>
          <IonTitle style={{ "--color": "white" }}>User Management</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={getallUsers} disabled={isLoading}>
              <IonIcon icon={refreshOutline} slot="icon-only" />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding modern-content">
        {/* Loading State */}
        {isLoading && (
          <div className="loading-container">
            <IonSpinner name="crescent" style={{ "--color": colors.primary }} />
            <IonText color="medium">Loading users...</IonText>
          </div>
        )}

        {/* User list */}
        {!isLoading && (
          <IonCard className="modern-card">
            <IonCardHeader className="modern-card-header">
              <div className="header-content">
                <IonCardTitle className="modern-title" color="black">
                  <IonIcon icon={personOutline} className="title-icon" />
                  User Accounts
                </IonCardTitle>
                <IonChip color="black">{totalUsers} Total Users</IonChip>
              </div>
              <IonText color="black" className="pagination-info">
                Showing {startIndex + 1}-{Math.min(endIndex, totalUsers)} of{" "}
                {totalUsers} users
              </IonText>
            </IonCardHeader>

            <IonCardContent>
              <IonList className="modern-list">
                {currentUsers.map((user: any) => (
                  <IonItem
                    key={user?.id}
                    className="modern-list-item"
                    button
                    detail={false}
                  >
                    <div className="user-avatar">
                      <IonIcon
                        icon={getStatusIcon(user?.account_status)}
                        style={{ color: getStatusColor(user?.account_status) }}
                      />
                    </div>

                    <IonLabel className="user-info">
                      <h2 className="user-name">
                        {user?.firstName} {user?.lastName}
                      </h2>
                      <p className="user-email">{user?.email}</p>
                      <div className="user-tags">
                        <IonBadge
                          className="status-badge"
                          style={{
                            "--background": getStatusColor(
                              user?.account_status
                            ),
                            "--color": "white",
                          }}
                        >
                          {user?.account_status}
                        </IonBadge>
                        <IonBadge color="medium" className="role-badge">
                          {user?.role}
                        </IonBadge>
                      </div>
                    </IonLabel>

                    <IonButton
                      fill="clear"
                      size="small"
                      className="manage-button"
                      onClick={() => {
                        setSelectedUser(user);
                        setShowModal(true);
                      }}
                    >
                      Manage
                      <IonIcon icon={chevronForwardOutline} slot="end" />
                    </IonButton>
                  </IonItem>
                ))}
              </IonList>

              {/* Enhanced Pagination Controls */}
              {totalPages > 1 && (
                <div className="modern-pagination">
                  <IonButton
                    size="small"
                    fill="clear"
                    disabled={currentPage === 1}
                    onClick={prevPage}
                    className="pagination-button"
                  >
                    Previous
                  </IonButton>

                  <div className="page-numbers">
                    {getPageNumbers().map((page) => (
                      <IonButton
                        key={page}
                        size="small"
                        fill={currentPage === page ? "solid" : "clear"}
                        color={currentPage === page ? "primary" : "medium"}
                        onClick={() => goToPage(page)}
                        className="page-button"
                      >
                        {page}
                      </IonButton>
                    ))}
                  </div>

                  <IonButton
                    size="small"
                    fill="clear"
                    disabled={currentPage === totalPages}
                    onClick={nextPage}
                    className="pagination-button"
                  >
                    Next
                  </IonButton>
                </div>
              )}
            </IonCardContent>
          </IonCard>
        )}

        {/* Audit Logs */}
        {!isLoading && (
          <IonCard className="modern-card">
            <IonCardHeader className="modern-card-header">
              <IonCardTitle className="modern-title">
                <IonIcon
                  icon={timeOutline}
                  className="title-icon"
                  color="white"
                />
                Audit Logs
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonList className="modern-list">
                {auditLogs?.length === 0 ? (
                  <IonItem>
                    <IonLabel className="no-logs">
                      <IonText color="medium">
                        No audit actions recorded yet.
                      </IonText>
                    </IonLabel>
                  </IonItem>
                ) : (
                  auditLogs.slice(0, 5).map((log: any, idx) => (
                    <IonItem key={idx} className="audit-log-item">
                      <IonLabel>
                        <p className="log-action">
                          <strong>{log.activity}</strong>
                        </p>
                        <div className="log-details">
                          <IonText color="medium" className="log-date">
                            {log.date}
                          </IonText>
                          {log.user && (
                            <IonText color="primary" className="log-user">
                              • by {log.user}
                            </IonText>
                          )}
                        </div>
                      </IonLabel>
                    </IonItem>
                  ))
                )}
              </IonList>
            </IonCardContent>
          </IonCard>
        )}

        {/* Manage User Modal */}
        <IonModal
          isOpen={showModal}
          onDidDismiss={() => setShowModal(false)}
          className="modern-modal"
        >
          <IonHeader className="modern-header">
            <IonToolbar style={{ "--background": colors.primary }}>
              <IonTitle style={{ "--color": "white" }}>
                Manage {selectedUser?.firstName}
              </IonTitle>
              <IonButtons slot="end">
                <IonButton
                  onClick={() => setShowModal(false)}
                  style={{ "--color": "white" }}
                  disabled={isUpdating}
                >
                  Close
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>

          <IonContent className="ion-padding modern-content">
            {selectedUser && (
              <div className="modal-content">
                {isUpdating && (
                  <div className="updating-overlay">
                    <IonSpinner name="crescent" />
                    <IonText>Updating...</IonText>
                  </div>
                )}

                {/* Status Section */}
                <div className="setting-section">
                  <h3 className="section-title">Account Status</h3>
                  <IonSelect
                    value={selectedUser.account_status}
                    placeholder="Select Status"
                    onIonChange={(e) =>
                      setSelectedUser((prev: any) => ({
                        ...prev,
                        account_status: e.detail.value,
                      }))
                    }
                    className="modern-select"
                  >
                    <IonSelectOption value="APPROVED">Approved</IonSelectOption>
                    <IonSelectOption value="Suspended">
                      Suspended
                    </IonSelectOption>
                    <IonSelectOption value="Banned">Banned</IonSelectOption>
                  </IonSelect>
                  <IonButton
                    expand="block"
                    className="action-button"
                    onClick={() => accountStatus(selectedUser)}
                    disabled={isUpdating}
                  >
                    Update Status
                  </IonButton>
                </div>

                {/* Role Section */}
                {/* <div className="setting-section">
                  <h3 className="section-title">Role Assignment</h3>
                  <IonSelect
                    value={selectedUser.role}
                    placeholder="Select Role"
                    onIonChange={(e) =>
                      setSelectedUser((prev: any) => ({
                        ...prev,
                        role: e.detail.value,
                      }))
                    }
                    className="modern-select"
                  >
                    <IonSelectOption value="Beneficiary">
                      Beneficiary
                    </IonSelectOption>
                    <IonSelectOption value="Vendor">Vendor</IonSelectOption>
                    <IonSelectOption value="Donor">Donor</IonSelectOption>
                  </IonSelect>
                  <IonButton
                    expand="block"
                    className="action-button"
                    onClick={() => assignRole(selectedUser)}
                    disabled={isUpdating}
                  >
                    Update Role
                  </IonButton>
                </div> */}
              </div>
            )}
          </IonContent>
        </IonModal>
      </IonContent>

      <style>{`
      /* UserManagement.css */
.modern-user-management {
  --modern-spacing: 16px;
  --border-radius: 12px;
  --shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.modern-header {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.modern-content {
  --background: #f8fafc;
}

.modern-card {
  border-radius: var(--border-radius);
  box-shadow: var(--shadow);
  margin-bottom: 20px;
  overflow: hidden;
}

.modern-card-header {
  padding: 20px;
  background:  #667eea ;
  color: white;
}

.modern-card-header .header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.modern-title {
  display: flex;
  align-items: center;
  font-size: 1.4em;
  font-weight: 600;
}

.title-icon {
  margin-right: 8px;
  font-size: 1.2em;
}

.pagination-info {
  font-size: 0.9em;
  opacity: 0.9;
}

.modern-list {
  background: transparent;
}

.modern-list-item {
  --padding-start: 16px;
  --padding-end: 16px;
  --inner-padding-end: 8px;
  border-radius: 8px;
  margin-bottom: 8px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.modern-list-item:hover {
  border-color: #3880ff;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(56, 128, 255, 0.1);
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-size: 1.2em;
}

.user-info {
  margin: 12px 0;
}

.user-name {
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.user-email {
  color: #64748b;
  font-size: 0.9em;
  margin-bottom: 8px;
}

.user-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.status-badge, .role-badge {
  border-radius: 6px;
  font-size: 0.75em;
  font-weight: 500;
  padding: 4px 8px;
}

.manage-button {
  --padding-start: 12px;
  --padding-end: 12px;
  font-weight: 500;
}

/* Pagination Styles */
.modern-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
  gap: 8px;
  flex-wrap: wrap;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.pagination-button, .page-button {
  --border-radius: 8px;
  min-width: 40px;
  height: 40px;
  font-weight: 500;
}

/* Loading States */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
}

.updating-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  gap: 12px;
}

/* Modal Styles */
.modern-modal .modal-content {
  position: relative;
}

.setting-section {
  margin-bottom: 32px;
}

.section-title {
  color: #1e293b;
  font-weight: 600;
  margin-bottom: 16px;
  font-size: 1.1em;
}

.modern-select {
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 16px;
  --padding-start: 12px;
}

.modern-select:hover {
  border-color: #3880ff;
}

.action-button {
  --border-radius: 8px;
  height: 48px;
  font-weight: 600;
  margin-top: 8px;
}

/* Audit Logs */
.audit-log-item {
  --padding-start: 0;
  --inner-padding-end: 0;
  border-bottom: 1px solid #f1f5f9;
}

.audit-log-item:last-child {
  border-bottom: none;
}

.log-action {
  margin-bottom: 4px;
  color: #334155;
}

.log-details {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.log-date, .log-user {
  font-size: 0.85em;
}

.no-logs {
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
}

/* Responsive Design */
@media (max-width: 768px) {
  .modern-card-header .header-content {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
  
  .modern-pagination {
    flex-direction: column;
    gap: 12px;
  }
  
  .page-numbers {
    order: -1;
  }
  
  .user-tags {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 480px) {
  .modern-list-item {
    --padding-start: 12px;
    --padding-end: 12px;
  }
  
  .user-avatar {
    width: 32px;
    height: 32px;
    font-size: 1em;
  }
}
      `}</style>
    </IonPage>
  );
};

export default UserManagement;
